import React, { useRef, useEffect } from "react";
import { Quote, Star } from "lucide-react";
import VanillaTilt from "vanilla-tilt";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/autoplay";
import { Autoplay } from "swiper/modules";
import "./ClientStories.css";
import shopaz from "../../assets/images/shopaz.png";
import procredit from "../../assets/images/procredit.png";
import burgerking from "../../assets/images/burgerking.png";
import Hib from "../../assets/images/HIB.png";
import neptun from "../../assets/images/neptun.png";
import trimed from "../../assets/images/trimed.png";
import elka from "../../assets/images/elka.png";
import vfs from "../../assets/images/vfs-min.png";
import prmall from "../../assets/images/prishtina-mall.svg";
import rugove from "../../assets/images/rugove.png";
import ltb from "../../assets/images/ltb.png";
import megatek from "../../assets/images/megatek.png";

const testimonials = [
  {
    id: 1,
    name: "Shopaz",
    role: "E-Commerce Platform",
    logo: shopaz,
    feedback:
      "Working with Katrori made a big difference for us. They helped streamline how new sellers get onboarded and made our logistics run smoother, especially during busy periods. It's been a huge help for our team and our customers.",
    rating: 5,
  },
  {
    id: 2,
    name: "Procredit Bank",
    role: "Financial Services",
    logo: procredit,
    feedback:
      "Katrori has been a solid partner in helping us implement and manage Business Central for our finance team. Their support has made our internal processes more efficient and reliable, and it's been a smooth experience working with them.",
    rating: 5,
  },
  {
    id: 3,
    name: "Burger King",
    role: "QSR Chain",
    logo: burgerking,
    feedback:
      "Managing operations across multiple countries isn’t easy, but Katrori has made a real difference for us. With their support on Business Central in Kosovo, Albania, and Macedonia, we’ve been able to keep our financial processes aligned and efficient. They've been consistent and dependable every step of the way.",
    rating: 5,
  },
  {
    id: 4,
    name: "HIB",
    role: "Fueling Company",
    logo: Hib,
    feedback:
      "The mobile app Katrori built for us has become a key part of how we connect with our customers. From discounts to giveaways, it’s helped us boost engagement and offer a better experience at our stations. The team was great at turning our ideas into something simple and effective.",
    rating: 5,
  },
  {
    id: 5,
    name: "Neptun",
    role: "Retail / Appliances",
    logo: neptun,
    feedback:
      "Katrori helped us bring our systems together across online and physical stores. Having a clear view of our inventory and customer data has made a big difference — it’s easier to manage and has improved the overall shopping experience for our customers.",
    rating: 5,
  },

  {
    id: 6,
    name: "Trimed",
    role: "Medical / Healthcare",
    logo: trimed,
    feedback:
      "Katrori helped us get Business Central up and running for our warehouse and finance operations, and it’s made a noticeable difference. Things are more organized, easier to track, and overall, more efficient. Their support has been steady and dependable.",
    rating: 5,
  },
  {
    id: 7,
    name: "Elka",
    role: "Consumer Goods",
    logo: elka,
    feedback:
      "Working with Katrori has helped us get a better handle on our supply chain. Their tools made it easier to see what’s happening in real time and reduce delays. They’ve been responsive and easy to work with throughout the process.",
    rating: 5,
  },
  {
    id: 8,
    name: "VFS",
    role: "Viva Fresh Store",
    logo: vfs,
    feedback:
      "Katrori has helped us manage our operations more effectively with Business Central and their HR app. It's made day-to-day work like tracking holidays and managing staff a lot more straightforward. They really understand what we need.",
    rating: 5,
  },
  {
    id: 9,
    name: "Prishtina Mall",
    role: "Shopping Center",
    logo: prmall,
    feedback:
      "Katrori has been a great tech partner for us. Their team helped simplify how we manage operations and services in the mall, making everything from scheduling to logistics more manageable. They’ve been very reliable.",
    rating: 5,
  },
  {
    id: 10,
    name: "Rugove",
    role: "Water & Dairy Products",
    logo: rugove,
    feedback:
      "Katrori’s solutions have helped us streamline our supply chain and inventory management, making it easier to keep our products fresh and available. Their team has been reliable and attentive to our specific needs.",
    rating: 5,
  },
  {
    id: 11,
    name: "LTB",
    role: "Fashion Retail",
    logo: ltb,
    feedback:
      "Katrori really helped take our online store to the next level. The new design works great on mobile, and it’s much easier for our customers to shop. We've definitely seen a boost since launching it.",
    rating: 5,
  },
  {
    id: 12,
    name: "Megatek",
    role: "Hypermarket / Office goods retailer",
    logo: megatek,
    feedback:
      "The training platform Katrori developed for us has helped improve how we onboard and train staff across departments. It's clear, easy to use, and has made internal training much more consistent.",
    rating: 5,
  },
];

const ClientStories = () => {
  const tiltRefs = useRef([]);

  const initializeTilt = (elements) => {
    elements.forEach((el) => {
      if (el && !el.vanillaTiltInitialized) {
        VanillaTilt.init(el, {
          max: 8,
          speed: 400,
          glare: true,
          "max-glare": 0.1,
        });
        el.vanillaTiltInitialized = true;
      }
    });
  };

  useEffect(() => {
    return () => {
      tiltRefs.current.forEach((el) => {
        if (el && el.vanillaTiltInitialized && el.vanillaTilt) {
          el.vanillaTilt.destroy();
          el.vanillaTiltInitialized = false;
        }
      });
    };
  }, []);

  return (
    <section id="client-stories" className="client-stories-section">
      <div className="container">
        <div className="section-header">
          <h2 className="section-title">
            <span className="holographic-text">CLIENTS' SUCCESS STORIES</span>
          </h2>
          <p className="section-subtitle">
            Industry leaders trust our{" "}
            <span className="highlight">next-generation solutions</span>
          </p>
        </div>

        <Swiper
          modules={[Autoplay]}
          spaceBetween={30}
          slidesPerView={1}
          loop={true}
          centeredSlides={true}
          autoplay={{
            delay: 0,
            disableOnInteraction: false,
            pauseOnMouseEnter: true,
          }}
          speed={3000}
          allowTouchMove={true}
          pagination={false}
          navigation={false}
          breakpoints={{
            768: {
              slidesPerView: 2,
              spaceBetween: 40,
            },
            1024: {
              slidesPerView: 3,
              spaceBetween: 50,
            },
          }}
          onSwiper={(swiper) => {
            initializeTilt(
              swiper.slides.map((slideEl) =>
                slideEl.querySelector(".testimonial-card")
              )
            );
          }}
          onTransitionEnd={(swiper) => {
            initializeTilt(
              swiper.slides.map((slideEl) =>
                slideEl.querySelector(".testimonial-card")
              )
            );
          }}
          className="testimonial-carousel-swiper"
        >
          {testimonials.map((testimonial, index) => (
            <SwiperSlide key={`${testimonial.id}-${index}`}>
              <div
                ref={(el) => {
                  if (el) tiltRefs.current[index] = el;
                }}
                className="testimonial-card"
              >
                <div className="card-aura"></div>
                <div className="tech-border"></div>
                <Quote className="quote-icon" size={28} />
                <p className="testimonial-text">
                  <span className="tech-quote">"</span>
                  {testimonial.feedback}
                  <span className="tech-quote">"</span>
                </p>
                <div className="client-info">
                  <div className="logo-halo">
                    <img
                      src={testimonial.logo}
                      alt={testimonial.name}
                      className="client-logo"
                    />
                  </div>
                  <div className="client-details">
                    <h4 className="client-name">{testimonial.name}</h4>
                    <p className="client-role">{testimonial.role}</p>
                    <div className="rating">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          size={18}
                          fill={
                            i < testimonial.rating ? "currentColor" : "none"
                          }
                          className={i < testimonial.rating ? "filled" : ""}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </section>
  );
};

export default ClientStories;
